import Script from "next/script";
import { useSession } from "next-auth/react";
import { usePylonEnabled } from "@/utils/hooks/use-pylon-enabled";
import React, { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { Icon } from "@/components/icons/Icon";
import { LocalStorageKey } from "@/utils/local-storage-utils";
import { useLocalStorageSyncedState } from "@/utils/hooks/use-local-storage-synced-state";
import toast from "react-hot-toast";

const PYLON_APP_ID = "ab31f62a-42b5-42a0-840e-da99099a1016";
const PYLON_CHAT_BUBBLE_HOVER_ZONE_PX = 96;

interface PylonChatBubbleState extends Record<string, unknown> {
  hidden: boolean;
}

const DEFAULT_PYLON_CHAT_BUBBLE_STATE: PylonChatBubbleState = { hidden: true };

type PylonCommand =
  | "hide"
  | "hideChatBubble"
  | "onChangeUnreadMessagesCount"
  | "onHide"
  | "onShow"
  | "setNewIssueCustomFields"
  | "setTheme"
  | "setTicketFormFields"
  | "setVisibleTicketForms"
  | "show"
  | "showChatBubble"
  | "showKnowledgeBaseArticle"
  | "showNewMessage"
  | "showTicketForm";

declare global {
  interface Window {
    Pylon: (command: PylonCommand, ...args: unknown[]) => void;
    pylon?: {
      chat_settings: {
        app_id: string;
        email: string;
        name: string;
      };
    };
  }
}

const isPylonChatBubbleHidden = (state: PylonChatBubbleState | boolean) => {
  if (typeof state === "boolean") {
    return state;
  }

  return state.hidden === true;
};

export const usePylonChatBubbleControls = () => {
  const [chatBubbleState, setChatBubbleState] = useLocalStorageSyncedState<PylonChatBubbleState>(
    LocalStorageKey.PYLON_CHAT_BUBBLE_HIDDEN,
    DEFAULT_PYLON_CHAT_BUBBLE_STATE,
  );
  const isChatBubbleHidden = isPylonChatBubbleHidden(chatBubbleState);

  const setPylonChatBubbleHidden = useCallback(
    (hidden: boolean, options: { syncWidgetVisibility?: boolean } = {}) => {
      const { syncWidgetVisibility = true } = options;
      setChatBubbleState({ hidden });
      if (syncWidgetVisibility) {
        window.Pylon?.(hidden ? "hideChatBubble" : "showChatBubble");
      }
    },
    [setChatBubbleState],
  );

  const hideChatBubble = useCallback(() => {
    setPylonChatBubbleHidden(true);
    toast.success("Chat bubble hidden. Use the account menu to show it again.");
  }, [setPylonChatBubbleHidden]);

  const showChatBubble = useCallback(() => {
    setPylonChatBubbleHidden(false);
  }, [setPylonChatBubbleHidden]);

  // Clear Chalk's hidden-bubble preference without forcing Pylon into launcher mode.
  const markChatBubbleVisible = useCallback(() => {
    setPylonChatBubbleHidden(false, { syncWidgetVisibility: false });
  }, [setPylonChatBubbleHidden]);

  return {
    hideChatBubble,
    isChatBubbleHidden,
    markChatBubbleVisible,
    showChatBubble,
  };
};

// Whether Pylon's window is open. Only Pylon's own callbacks report it, so they are registered
// once for the whole app, by the first reader that finds the widget's stub in place.
let pylonWindowOpen = false;
let pylonWindowCallbacksRegistered = false;
const pylonWindowOpenListeners = new Set<() => void>();

const setPylonWindowOpen = (open: boolean) => {
  if (pylonWindowOpen === open) return;
  pylonWindowOpen = open;
  pylonWindowOpenListeners.forEach((listener) => listener());
};

/**
 * Registers Pylon's show and hide callbacks if they are not yet, once the widget's stub exists. The
 * stub queues them until the widget loads. Call it before opening the window, since the stub can
 * arrive after a reader first mounts.
 */
export const ensurePylonWindowCallbacks = () => {
  if (pylonWindowCallbacksRegistered || typeof window.Pylon !== "function") return;
  pylonWindowCallbacksRegistered = true;
  window.Pylon("onShow", () => setPylonWindowOpen(true));
  window.Pylon("onHide", () => setPylonWindowOpen(false));
};

const subscribeToPylonWindowOpen = (listener: () => void) => {
  pylonWindowOpenListeners.add(listener);
  ensurePylonWindowCallbacks();
  return () => {
    pylonWindowOpenListeners.delete(listener);
  };
};

/** Whether Pylon's chat window is open. */
export const usePylonWindowOpen = (): boolean =>
  useSyncExternalStore(
    subscribeToPylonWindowOpen,
    () => pylonWindowOpen,
    () => false,
  );

export interface PylonChatBubbleHideButtonProps {
  enabled: boolean;
}

export const PylonChatBubbleHideButton: React.FC<PylonChatBubbleHideButtonProps> = ({
  enabled,
}) => {
  const { hideChatBubble, isChatBubbleHidden } = usePylonChatBubbleControls();
  const [isHoveringChatBubble, setIsHoveringChatBubble] = useState(false);

  useEffect(() => {
    if (!enabled || isChatBubbleHidden) {
      setIsHoveringChatBubble((wasHoveringChatBubble) =>
        wasHoveringChatBubble ? false : wasHoveringChatBubble,
      );
      return;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const isInChatBubbleHoverZone =
        event.clientX >= window.innerWidth - PYLON_CHAT_BUBBLE_HOVER_ZONE_PX &&
        event.clientY >= window.innerHeight - PYLON_CHAT_BUBBLE_HOVER_ZONE_PX;
      setIsHoveringChatBubble((wasHoveringChatBubble) =>
        wasHoveringChatBubble === isInChatBubbleHoverZone
          ? wasHoveringChatBubble
          : isInChatBubbleHoverZone,
      );
    };

    const handlePointerLeave = () =>
      setIsHoveringChatBubble((wasHoveringChatBubble) =>
        wasHoveringChatBubble ? false : wasHoveringChatBubble,
      );

    const listenerOptions: AddEventListenerOptions = { passive: true };
    window.addEventListener("pointermove", handlePointerMove, listenerOptions);
    window.addEventListener("pointerleave", handlePointerLeave, listenerOptions);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove, listenerOptions);
      window.removeEventListener("pointerleave", handlePointerLeave, listenerOptions);
    };
  }, [enabled, isChatBubbleHidden]);

  if (!enabled || isChatBubbleHidden || !isHoveringChatBubble) {
    return null;
  }

  return (
    <button
      type="button"
      className="fixed bottom-[58px] right-[52px] z-[60] inline-flex h-5 w-5 items-center justify-center rounded-full border border-cds-gray-300 bg-white text-cds-text-secondary shadow-md transition-colors hover:bg-cds-gray-50 hover:text-cds-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cds-bright-green-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
      aria-label="Hide chat bubble"
      onClick={hideChatBubble}
    >
      <Icon name="close" size="xs" />
    </button>
  );
};

// Need to add '<script>
// (function(){var e=window;var t=document;var n=function(){n.e(arguments)};n.q=[];n.e=function(e){n.q.push(e)};e.Pylon=n;var r=function(){var e=t.createElement("script");e.setAttribute("type","text/javascript");e.setAttribute("async","true");e.setAttribute("src","https://widget.usepylon.com/widget/APP_ID");var n=t.getElementsByTagName("script")[0];n.parentNode.insertBefore(e,n)};if(t.readyState==="complete"){r()}else if(e.addEventListener){e.addEventListener("load",r,false)}})();
// </script
// Then, need to call this:
// ``` window.pylon = {
//   chat_settings: {
//     app_id: "APP_ID",
//     email: "USER_EMAIL",
//     name: "USER_FULL_NAME",

//     // (Optional) If you have the user's profile image available through something like Auth0
//     avatar_url: "USER_PROFILE_IMAGE_URL",

//     // (Optional) If you are using Pylon's identity verification
//     email_hash: "USER_EMAIL_HASH",

//     // (Optional) If you want to force association of an issue/contact with a certain account.
//     account_id: "057a3564-fc1b-4224-9292-dcf718653452",

//     // (Optional) If you want to force association of an issue/contact with a certain account.
//     // Learn more about external IDs here: https://docs.usepylon.com/pylon-docs/developer/api/external-ids.
//     account_external_id: "ab38e36b-3e28-4e2d-b976-6282735b77eb"
//   },
// };

export function usePylon(
  userId: string | null | undefined,
  userEmail: string | null | undefined,
  userName: string | null | undefined,
) {
  const enabled = usePylonEnabled();
  const hasPylonUser = enabled && userId != null && userEmail != null && userName != null;
  const [chatBubbleState] = useLocalStorageSyncedState<PylonChatBubbleState>(
    LocalStorageKey.PYLON_CHAT_BUBBLE_HIDDEN,
    DEFAULT_PYLON_CHAT_BUBBLE_STATE,
  );
  const isChatBubbleHidden = isPylonChatBubbleHidden(chatBubbleState);

  useEffect(() => {
    if (!hasPylonUser) {
      delete window.pylon;
      window.Pylon?.("hide");
      window.Pylon?.("hideChatBubble");
      setPylonWindowOpen(false);
      return;
    }

    window.pylon = {
      chat_settings: {
        app_id: PYLON_APP_ID,
        email: userEmail,
        name: userName,
      },
    };
    window.Pylon?.(isChatBubbleHidden ? "hideChatBubble" : "showChatBubble");
  }, [hasPylonUser, isChatBubbleHidden, userEmail, userName]);

  useEffect(
    () => () => {
      delete window.pylon;
      window.Pylon?.("hide");
      window.Pylon?.("hideChatBubble");
      setPylonWindowOpen(false);
    },
    [],
  );

  return { hasPylonUser };
}

export const PylonIntegration: React.FC = () => {
  const session = useSession().data;
  const { hasPylonUser } = usePylon(session?.userId, session?.user?.email, session?.user?.name);
  return hasPylonUser ? (
    <>
      <PylonInit />
      <PylonChatBubbleHideButton enabled />
    </>
  ) : null;
};

export default function PylonInit() {
  const { isChatBubbleHidden } = usePylonChatBubbleControls();
  return (
    <Script
      id="pylon-init"
      onReady={() => {
        // The script can finish loading after the active team loses Pylon access.
        if (window.pylon == null) {
          window.Pylon?.("hide");
          window.Pylon?.("hideChatBubble");
        } else {
          window.Pylon?.(isChatBubbleHidden ? "hideChatBubble" : "showChatBubble");
        }
      }}
      dangerouslySetInnerHTML={{
        __html: `(function(){
          var e=window;
          var t=document;
          var n=function(){n.e(arguments)};
          n.q=[];
          n.e=function(e){n.q.push(e)};
          e.Pylon=n;
          var r=function(){
            var e=t.createElement("script");
            e.setAttribute("type","text/javascript");
            e.setAttribute("async","true");
            e.setAttribute("src","https://widget.usepylon.com/widget/${PYLON_APP_ID}");
            var n=t.getElementsByTagName("script")[0];
            n.parentNode.insertBefore(e,n)
          };
          if(t.readyState==="complete"){
            r()
          } else if(e.addEventListener){
            e.addEventListener("load",r,false)
          }
        })();`,
      }}
    />
  );
}
